import React from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { PeriodDropdown, PeriodOption } from './PeriodDropdown';
import { DateRangePicker } from './DateRangePicker';
import type { CustomDateRange } from '../types';

interface DailyChallengeStat {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  neutral?: boolean; // If true, show grey text without arrow
}

interface DailyChallengeCardProps {
  periodTitle: string;
  mainValue: string;
  mainChange: string;
  mainChangePositive: boolean;
  successRate: string;
  stats: DailyChallengeStat[];
  theme: ThemeColors;
  isMobile: boolean;
  period: PeriodOption;
  onPeriodChange: (value: PeriodOption) => void;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange | undefined) => void;
}

export const DailyChallengeCard: React.FC<DailyChallengeCardProps> = ({
  periodTitle,
  mainValue,
  mainChange,
  mainChangePositive,
  successRate,
  stats,
  theme,
  isMobile,
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
}) => {
  // Check if change is 0% (no change)
  const isNoChange = mainChange.includes('+0.00%') || mainChange.includes('-0.00%');

  const styles = {
    card: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      position: 'relative' as const,
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    },
    statLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginBottom: '6px',
      fontWeight: 500,
    },
    statValue: {
      fontSize: isMobile ? '28px' : '32px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '6px',
      lineHeight: 1.1,
    },
    statChange: {
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 500,
    },
  };

  return (
    <div style={styles.card}>
      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
        <PeriodDropdown
          value={period}
          onChange={onPeriodChange}
          theme={theme}
          style={{ position: 'relative', top: 'auto', right: 'auto' }}
        />
        {onCustomRangeChange && (
          <DateRangePicker
            value={customRange}
            onChange={onCustomRangeChange}
            theme={theme}
            style={{ position: 'relative', top: 'auto', right: 'auto' }}
            isActive={period === 'custom'}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          gap: '20px',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <div style={styles.statLabel}>{periodTitle} Challenges</div>
          <div style={styles.statValue}>{mainValue}</div>
          <div
            style={{
              ...styles.statChange,
              color: isNoChange ? theme.textSecondary : (mainChangePositive ? '#22c55e' : '#ef4444'),
              marginBottom: '4px',
            }}
          >
            {isNoChange ? (
              <span>No Change from yesterday</span>
            ) : (
              <><span>{mainChangePositive ? '↑' : '↓'}</span> {mainChange}</>
            )}
          </div>
          <div style={{ ...styles.statChange, color: theme.textSecondary }}>
            {successRate} Success rate
          </div>
        </div>
        <div style={{ flex: 1, fontSize: '12px', lineHeight: 1.8, marginTop: '8px' }}>
          {stats.map((s) => {
            const statIsNoChange = s.change.includes('+0.00%') || s.change.includes('-0.00%');
            const isNeutral = s.neutral || statIsNoChange;
            return (
              <div
                key={s.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <span style={{ color: theme.textSecondary }}>{s.label}</span>
                <span style={{ fontWeight: 600, minWidth: '36px', color: theme.text }}>
                  {s.value}
                </span>
                <span
                  style={{
                    color: isNeutral ? theme.textSecondary : (s.positive ? '#22c55e' : '#ef4444'),
                    minWidth: '70px',
                    textAlign: 'right',
                  }}
                >
                  {statIsNoChange ? 'No Change' : (isNeutral ? s.change : <>{s.positive ? '↑' : '↓'} {s.change}</>)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
